import { useRef, useEffect } from 'react';
import styled from 'styled-components';
import DOMPurify from 'isomorphic-dompurify';

const ALLOWED_URL_SCHEMES = /^(https?:|mailto:|tel:)/i;

const sanitize = (html) => DOMPurify.sanitize(html || '', {
  ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 's', 'strike', 'a', 'br', 'p', 'div', 'span', 'ul', 'ol', 'li'],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
  ALLOW_DATA_ATTR: false,
});

const exec = (cmd, value) => document.execCommand(cmd, false, value);

const RichEditor = ({ value = '', onChange, placeholder = '내용을 입력하세요', minHeight = 180 }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && value !== ref.current.innerHTML) {
      ref.current.innerHTML = sanitize(value);
    }
  }, [value]);

  const handleInput = () => {
    const html = sanitize(ref.current?.innerHTML || '');
    onChange?.(html);
  };

  const handleLink = () => {
    const url = window.prompt('URL을 입력해 주세요. (http, https, mailto, tel 만 허용)');
    if (!url) return;
    const trimmed = url.trim();
    if (!ALLOWED_URL_SCHEMES.test(trimmed)) {
      alert('허용되지 않는 URL 스킴입니다. http(s):, mailto:, tel: 만 가능합니다.');
      return;
    }
    exec('createLink', trimmed);
    handleInput();
  };

  const runExec = (cmd) => {
    exec(cmd);
    handleInput();
  };

  const toolBtn = (label, onAction, title) => (
    <ToolBtn
      type="button"
      title={title || label}
      onMouseDown={(e) => { e.preventDefault(); onAction(); }}
    >
      {label}
    </ToolBtn>
  );

  return (
    <Wrap>
      <Toolbar>
        {toolBtn('B', () => runExec('bold'), '굵게')}
        {toolBtn('I', () => runExec('italic'), '기울임')}
        {toolBtn('U', () => runExec('underline'), '밑줄')}
        {toolBtn('S', () => runExec('strikeThrough'), '취소선')}
        <Divider />
        {toolBtn('링크', handleLink, '링크 삽입')}
        {toolBtn('해제', () => runExec('removeFormat'), '서식 해제')}
      </Toolbar>
      <Editable
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        data-placeholder={placeholder}
        $minHeight={minHeight}
      />
    </Wrap>
  );
};

export default RichEditor;
export { sanitize };

const Wrap = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  overflow: hidden;
  background: #fff;
  &:focus-within { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: #F9FAFB;
`;

const ToolBtn = styled.button`
  font-size: 12px;
  font-weight: 600;
  min-width: 26px;
  height: 26px;
  padding: 0 8px;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: transparent;
  &:hover { background: ${({ theme }) => theme.colors.borderLight}; color: ${({ theme }) => theme.colors.text}; }
`;

const Divider = styled.span`
  width: 1px;
  height: 14px;
  background: ${({ theme }) => theme.colors.border};
  margin: 0 4px;
`;

const Editable = styled.div`
  padding: 12px 14px;
  min-height: ${({ $minHeight }) => $minHeight}px;
  font-size: 13px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.text};
  outline: none;
  &:empty::before {
    content: attr(data-placeholder);
    color: ${({ theme }) => theme.colors.textLight};
  }
`;
